import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, IconButton, Chip, TextField, Paper,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  CircularProgress, Alert, Tooltip, Switch, FormControlLabel,
} from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import GroupsIcon from '@mui/icons-material/Groups';
import { StyledDialog, inputStyles } from './common';

const API_BASE = process.env.REACT_APP_API_URL || 'https://turismo.nara.chat/api';

function getEmpresaId() {
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    return localStorage.getItem('empresa_activa') || user.company_id || user.empresa_id || '1';
  } catch { return '1'; }
}

function getAuthHeaders() {
  const token = localStorage.getItem('token');
  return { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}

const cardStyle = {
  borderRadius: 4,
  background: 'rgba(255, 255, 255, 0.9)',
  boxShadow: '0 4px 24px rgba(0, 0, 0, 0.06)',
  border: '1px solid rgba(255, 255, 255, 0.5)',
  overflow: 'hidden',
};

const gradientIntermediario = 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)';

export default function AdminIntermediarios() {
  const [intermediarios, setIntermediarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [incluirInactivos, setIncluirInactivos] = useState(false);
  const [crearOpen, setCrearOpen] = useState(false);
  const [editar, setEditar] = useState(null);
  const empresaId = getEmpresaId();

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        empresa_id: empresaId,
        incluir_inactivos: String(incluirInactivos),
      });
      const res = await fetch(`${API_BASE}/admin/intermediarios/?${params}`, { headers: getAuthHeaders() });
      setIntermediarios(await res.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [empresaId, incluirInactivos]);

  useEffect(() => { cargar(); }, [cargar]);

  const handleDelete = async (u) => {
    if (!window.confirm(`¿Remover a "${u.name}" como intermediario? El usuario se conserva para auditoría histórica.`)) return;
    const res = await fetch(`${API_BASE}/admin/intermediarios/${u.id}`, { method: 'DELETE', headers: getAuthHeaders() });
    if (res.ok) cargar();
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography sx={{ color: '#0f172a', fontWeight: 700, fontSize: 22, mb: 0.5 }}>
            <GroupsIcon sx={{ fontSize: 24, verticalAlign: 'middle', mr: 1, color: '#8b5cf6' }} />
            Intermediarios
          </Typography>
          <Typography sx={{ color: '#64748b', fontSize: 13 }}>
            Empleados que procesan reservas de agencias vía WhatsApp. La reserva queda vinculada a la agencia; el intermediario solo en auditoría.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<PersonAddIcon />}
          onClick={() => setCrearOpen(true)}
          sx={{
            background: gradientIntermediario,
            boxShadow: '0 8px 20px rgba(139, 92, 246, 0.4)',
            textTransform: 'none', fontWeight: 600,
            '&:hover': { background: gradientIntermediario, opacity: 0.9 },
          }}>
          Nuevo intermediario
        </Button>
      </Box>

      <Box sx={{ mb: 2 }}>
        <FormControlLabel
          control={<Switch checked={incluirInactivos} onChange={e => setIncluirInactivos(e.target.checked)} size="small" />}
          label={<Typography sx={{ fontSize: 12, color: '#64748b' }}>Incluir inactivos</Typography>}
        />
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress /></Box>
      ) : intermediarios.length === 0 ? (
        <Box sx={{ ...cardStyle, p: 5, textAlign: 'center' }}>
          <GroupsIcon sx={{ fontSize: 60, color: '#cbd5e1', mb: 1.5 }} />
          <Typography sx={{ color: '#0f172a', fontSize: 16, fontWeight: 600 }}>
            Sin intermediarios registrados
          </Typography>
          <Typography sx={{ color: '#64748b', fontSize: 13, mt: 0.5 }}>
            Crea un intermediario para que pueda procesar reservas de agencias vía WhatsApp.
          </Typography>
        </Box>
      ) : (
        <TableContainer component={Paper} sx={cardStyle}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f8fafc' }}>
                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Nombre</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Email</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>WhatsApp</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 12 }} align="center">Estado</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 12 }} align="center">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {intermediarios.map(u => (
                <TableRow key={u.id} hover>
                  <TableCell>
                    <Typography sx={{ fontWeight: 600, fontSize: 13 }}>
                      {u.name} {u.lastname || ''}
                    </Typography>
                    <Chip label="INTERMEDIARIO" size="small"
                      sx={{ bgcolor: '#ede9fe', color: '#6d28d9', fontWeight: 700, fontSize: 9, height: 18, mt: 0.5 }} />
                  </TableCell>
                  <TableCell sx={{ fontSize: 12, color: '#64748b' }}>{u.email}</TableCell>
                  <TableCell sx={{ fontSize: 12 }}>
                    <WhatsAppIcon sx={{ fontSize: 14, color: '#25D366', verticalAlign: 'middle', mr: 0.5 }} />
                    {u.whatsapp || <span style={{ color: '#cbd5e1' }}>—</span>}
                  </TableCell>
                  <TableCell align="center">
                    {u.activo ? (
                      <Chip label="Activo" size="small" sx={{ bgcolor: '#dcfce7', color: '#15803d', fontWeight: 600, fontSize: 10 }} />
                    ) : (
                      <Chip label="Inactivo" size="small" sx={{ bgcolor: '#f1f5f9', color: '#64748b', fontSize: 10 }} />
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Editar">
                      <IconButton size="small" onClick={() => setEditar(u)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Remover como intermediario">
                      <IconButton size="small" color="error" onClick={() => handleDelete(u)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {crearOpen && (
        <CrearIntermediarioDialog onClose={() => setCrearOpen(false)} onCreated={() => { setCrearOpen(false); cargar(); }} />
      )}
      {editar && (
        <EditarIntermediarioDialog intermediario={editar} onClose={() => setEditar(null)} onUpdated={() => { setEditar(null); cargar(); }} />
      )}
    </Box>
  );
}


function CrearIntermediarioDialog({ onClose, onCreated }) {
  const [name, setName] = useState('');
  const [lastname, setLastname] = useState('');
  const [email, setEmail] = useState('');
  const [whatsapp, setWhatsapp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const empresaId = getEmpresaId();

  const submit = async () => {
    setError(null);
    if (!name.trim()) return setError('Falta el nombre.');
    if (!email.trim()) return setError('Falta el correo.');
    if (!whatsapp.trim()) return setError('Falta el WhatsApp.');

    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/admin/intermediarios/`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          name: name.trim(),
          lastname: lastname.trim(),
          email: email.trim(),
          whatsapp: whatsapp.trim(),
          empresa_id: empresaId,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Error');
      onCreated();
    } catch (e) {
      setError(e.message || 'Error creando intermediario');
    }
    setLoading(false);
  };

  return (
    <StyledDialog open onClose={onClose}
      title="Nuevo intermediario"
      subtitle="Empleado que procesa reservas de agencias vía WhatsApp"
      icon={<PersonAddIcon />} variant="compact" maxWidth="sm">
      <StyledDialog.Body compact>
        <Alert severity="info" sx={{ fontSize: 12, mb: 2 }}>
          El intermediario recibirá un link de reserva en su WhatsApp cuando una agencia le envíe un formulario por copia-pega.
        </Alert>
        <TextField fullWidth size="small" label="Nombre" sx={{ ...inputStyles, mb: 1.5 }}
          value={name} onChange={e => setName(e.target.value)} autoFocus />
        <TextField fullWidth size="small" label="Apellido (opcional)" sx={{ ...inputStyles, mb: 1.5 }}
          value={lastname} onChange={e => setLastname(e.target.value)} />
        <TextField fullWidth size="small" type="email" label="Email" sx={{ ...inputStyles, mb: 1.5 }}
          value={email} onChange={e => setEmail(e.target.value)} />
        <TextField fullWidth size="small" type="tel" label="WhatsApp" placeholder="+57..."
          helperText="El número desde el que enviará los formularios de las agencias"
          sx={inputStyles}
          value={whatsapp} onChange={e => setWhatsapp(e.target.value)} />
        {error && <Alert severity="error" sx={{ mt: 1.5, fontSize: 12 }}>{error}</Alert>}
      </StyledDialog.Body>
      <StyledDialog.CompactActions>
        <StyledDialog.CancelButton fullWidth onClick={onClose}>Cancelar</StyledDialog.CancelButton>
        <StyledDialog.PrimaryButton fullWidth loading={loading} onClick={submit}>
          Crear intermediario
        </StyledDialog.PrimaryButton>
      </StyledDialog.CompactActions>
    </StyledDialog>
  );
}


function EditarIntermediarioDialog({ intermediario, onClose, onUpdated }) {
  const [name, setName] = useState(intermediario.name || '');
  const [lastname, setLastname] = useState(intermediario.lastname || '');
  const [whatsapp, setWhatsapp] = useState(intermediario.whatsapp || '');
  const [activo, setActivo] = useState(intermediario.activo);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const submit = async () => {
    setError(null); setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/admin/intermediarios/${intermediario.id}`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify({ name, lastname, whatsapp, activo }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Error');
      onUpdated();
    } catch (e) { setError(e.message); }
    setLoading(false);
  };

  return (
    <StyledDialog open onClose={onClose}
      title={`Editar — ${intermediario.name}`}
      icon={<EditIcon />} variant="compact" maxWidth="sm">
      <StyledDialog.Body compact>
        <TextField fullWidth size="small" label="Nombre" sx={{ ...inputStyles, mb: 1.5 }}
          value={name} onChange={e => setName(e.target.value)} />
        <TextField fullWidth size="small" label="Apellido" sx={{ ...inputStyles, mb: 1.5 }}
          value={lastname} onChange={e => setLastname(e.target.value)} />
        <TextField fullWidth size="small" type="tel" label="WhatsApp" sx={{ ...inputStyles, mb: 1.5 }}
          value={whatsapp} onChange={e => setWhatsapp(e.target.value)} />
        <FormControlLabel
          control={<Switch checked={activo} onChange={e => setActivo(e.target.checked)} />}
          label={<Typography sx={{ fontSize: 13 }}>Activo</Typography>}
        />
        {error && <Alert severity="error" sx={{ mt: 1.5, fontSize: 12 }}>{error}</Alert>}
      </StyledDialog.Body>
      <StyledDialog.CompactActions>
        <StyledDialog.CancelButton fullWidth onClick={onClose}>Cancelar</StyledDialog.CancelButton>
        <StyledDialog.PrimaryButton fullWidth loading={loading} onClick={submit}>
          Guardar
        </StyledDialog.PrimaryButton>
      </StyledDialog.CompactActions>
    </StyledDialog>
  );
}
